import { useState } from "react";
import { organizationReviewStatusUpdate } from "../../services/reviewService";
import Modal from "../elements/Modal";
import Button from "../form/Button";
import Select from "../form/select/Select";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Indentification from "../elements/Indentification";
import dayjs from "dayjs";
import RatingStars from "../elements/RatingStars";

// Status options for review moderation
const REVIEW_STATUS_OPTIONS = [
  { name: "Pending", value: "pending" },
  { name: "Approved", value: "approved" },
  { name: "Rejected", value: "rejected" },
];

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case "approved":
      return "bg-green-100 text-green-700";
    case "rejected":
      return "bg-red-100 text-red-700";
    case "pending":
    default:
      return "bg-yellow-100 text-yellow-700";
  }
};

// Status Badge Component
const StatusBadge = ({ status, onClick }) => {
  const colorClass = getStatusColor(status);

  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 rounded-full text-xs font-medium capitalize transition-all duration-200 hover:shadow-md ${colorClass} hover:opacity-80`}
      title="Click to change status"
    >
      {status || "pending"}
    </button>
  );
};

const StatusUpdateModal = ({ isOpen, onClose, reviewData, onSuccess }) => {
  const [selectedStatus, setSelectedStatus] = useState("");
  const [reason, setReason] = useState("");

  // Initialize status when modal opens
  useState(() => {
    if (isOpen && reviewData) {
      setSelectedStatus(reviewData.status || "pending");
      setReason("");
    }
  }, [isOpen, reviewData]);

  // Status update mutation
  const { mutate: updateStatus, isPending: isUpdating } = useMutation({
    mutationFn: async (payload) => {
      const res = await organizationReviewStatusUpdate({
        ids: [reviewData._id],
        status: payload.status,
      });
      return res;
    },
    onSuccess: (res) => {
      if (res?.status === 200) {
        toast.success("Review status updated successfully!");
        onSuccess(res);
        onClose();
      } else {
        toast.warning(res?.message || "Failed to update status");
      }
    },
    onError: (error) => {
      console.error("Status update error:", error);
      toast.error(error?.message || "Failed to update review status");
    },
  });

  const handleSubmit = () => {
    if (!selectedStatus) {
      toast.warning("Please select a status");
      return;
    }

    updateStatus({
      status: selectedStatus,
      reason: reason.trim(),
    });
  };

  const handleClose = () => {
    setSelectedStatus("");
    setReason("");
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={handleClose}
      title="Update Review Status"
      size="md"
      titleIcon="fa-regular fa-clipboard-list"
      titleClass="!font-semibold !text-xl"
      headerClass="!text-black border-b border-gray-200 bg-gray-50 py-4 px-6"
      childrenClasses="!bg-white"
      bodyClasses="!py-6 !px-6"
    >
      <div className="space-y-6">
        {/* Review Info */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-3 mb-3">
            <Indentification
              className="shadow-sm bg-transparent border border-gray-200"
              type="circle"
              size="sm"
              image={reviewData?.userData?.image?.url}
              defaultClass="!bg-primary/10 !text-primary !font-semibold"
            />
            <div>
              <div className="font-semibold text-gray-900">
                {`${reviewData?.userData?.firstName ?? ""} ${
                  reviewData?.userData?.lastName ?? ""
                }`.trim() || "Anonymous"}
              </div>
              <div className="text-sm text-gray-600">
                {dayjs(reviewData?.createdAt).format("MMM DD, YYYY")}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <RatingStars data={reviewData?.rating} />
          </div>
          <p className="text-sm text-gray-700 line-clamp-3">
            {reviewData?.message || "No review message"}
          </p>
        </div>

        {/* Current Status */}
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-700">
            Current Status:
          </span>
          <StatusBadge status={reviewData?.status} />
        </div>

        {/* Status Selection */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            New Status <span className="text-red-500">*</span>
          </label>
          <Select
            placeholder="Select review status"
            dropdownData={REVIEW_STATUS_OPTIONS}
            selectedValue={selectedStatus}
            onChange={(value) => setSelectedStatus(value)}
            dropdownButtonClass="!h-12"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            buttonLabel="Cancel"
            variant="outline"
            buttonFunction={handleClose}
            isDisable={isUpdating}
          />
          <Button
            buttonLabel="Update Status"
            variant="secondary"
            buttonFunction={handleSubmit}
            {...(isUpdating && {
              loading: true,
              buttonIconPosition: "left",
              isDisable: true,
            })}
          />
        </div>
      </div>
    </Modal>
  );
};

export default StatusUpdateModal;
